/**
 * @file       authentication service
 * @version    2.0.0
 * @author     Mehran Majidi
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or
 *             altered in anyway without expressed written permission from
 *             MindShare HDV LLC.
 */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import
{
    BehaviorSubject,
    Observable,
    Subject,
    finalize,
    shareReplay
} from 'rxjs';

import { Idle } from '@ng-idle/core';

import { environment } from '@environments/environment';
import { USER_API } from '@app/constants/Api.constant';
import { SETTING_API } from '@app/constants/Api.constant';
import
{
    LOGOUT_QUERY_PARAM,
    LOGOUT_REASON
}
    from '@app/constants/Logout.constant';
import { LogoutReason }
    from '@app/secure/interface/LogoutReason.interface';

import { User } from '@app/model/user';
import { Setting } from '@app/model/setting';

import { PermissionsService } from '@app/service/permission.service';
import { PreferenceService } from '@app/service/preference.service';
import { SettingService } from '@app/service/setting.service';
import { ApiService } from '@app/secure/service/Api.service';
import { USER_ROLE, USER_TYPE } from '@app/constants/User.constant';

@Injectable
(
    { providedIn: 'root' }
)
class AuthService
{
    private static readonly MFA_VERIFIED_STORAGE_KEY = 'mfaVerified';
    private static readonly MFA_VERIFIED_STATE_KEY = 'mfaVerifiedState';
    private static readonly MFA_VERIFIED_TTL_MS = 8 * 60 * 60 * 1000;
    private user$: BehaviorSubject<User>;
    private returnUrlValue: string;
    private verifiedFlag: boolean;
    private allowBrowserSessionPersistence: boolean;
    private userSessionHydrationInProgress: boolean;
    /** One in-flight /token/refresh shared by parallel 401 handlers. */
    private tokenRefreshShared$: Observable<any> | null;
    /** Session id from login when cross-site cookies are blocked. */
    private cypherSessionIdValue: string;
    /** True while logout flow is clearing session and routing to login. */
    private logoutInProgressFlag: boolean;
    /** Last logout reason for login redirect messaging. */
    private logoutReasonValue: LogoutReason;

    //_____________________________ C O N S T R U C T O R ______________________
    /**
     * @desc   - component constructor
     * @access - public
     * @param  - router : router object
     *         - http : http object
     *         - permissionsService : permissions service
     *         - preferenceService : preference service
     *         - settingService : setting service
     *         - apiService : api service
     *
     * @return -
     */
    constructor
    (
        private router: Router,
        private http: HttpClient,
        private idle: Idle,
        private permissionsService: PermissionsService,
        private preferenceService: PreferenceService,
        private settingService: SettingService,
        private apiService: ApiService
    )
    {
        let storedUser: User;
        let storedUserJson: string;
        // having user in the local storage is not a good idea
        // because it persists and it has no way of clearing it.
        // Best to put it in a service

        storedUser = null;
        storedUserJson = localStorage.getItem('user');

        if (storedUserJson)
        {
            try
            {
                storedUser = JSON.parse(storedUserJson);
            }
            catch (e)
            {
                storedUser = null;
            }
        }

        this.user$ = new BehaviorSubject<User>(storedUser);

         this.returnUrlValue = '';
         this.verifiedFlag = false;
         this.syncVerifiedFlagFromStorage();
         this.allowBrowserSessionPersistence = true;
         this.userSessionHydrationInProgress = false;
         this.tokenRefreshShared$ = null;
         this.cypherSessionIdValue = storedUser?.sessionId || '';
         this.logoutInProgressFlag = false;
         this.logoutReasonValue = LOGOUT_REASON.MANUAL;

         window.addEventListener(
             'storage',
             (event: StorageEvent) =>
             {
                 this.onCrossTabMfaStorageEvent(event);
             }
         );
    }

    //______________________ S T A T I C  M E T H O D S ________________________

    //__________________ P R I V A T E  M E T H O D S __________________________
    /**
     * @desc   - read persisted MFA state JSON when valid for a user
     * @access - private
     * @param  - string uId : user id to match
     * @return - string | null : raw JSON or null
     */
    private getPreservedMfaStateForUser
    (
        uId: string
    ): string | null
    {
        let stateJson: string;
        let state: any;
        let ageMs: number;
        let result: string | null;

        result = null;

        if (!uId)
        {
            return result;
        }

        stateJson = localStorage.getItem(
            AuthService.MFA_VERIFIED_STATE_KEY
        ) || '';

        if (!stateJson)
        {
            return result;
        }

        try
        {
            state = JSON.parse(stateJson);
            ageMs = Date.now() - Number(state?.verifiedAt || 0);

            if (state?.uId === uId &&
                state?.verified === true &&
                ageMs >= 0 &&
                ageMs <= AuthService.MFA_VERIFIED_TTL_MS)
            {
                result = stateJson;
            }
        }
        catch (e)
        {
            result = null;
        }

        return result;
    }

    /**
     * @desc   - sync in-memory MFA flag from localStorage for current user
     * @access - private
     * @param  -
     * @return -
     */
    private syncVerifiedFlagFromStorage()
    {
        let uId: string;
        let preservedState: string | null;

        uId = this.userValue?.uId || '';
        preservedState = this.getPreservedMfaStateForUser(uId);
        this.verifiedFlag = preservedState ? true : false;
    }

    /**
     * @desc   - apply MFA state updates from another browser tab
     * @access - private
     * @param  - StorageEvent event : storage event payload
     * @return -
     */
    private onCrossTabMfaStorageEvent
    (
        event: StorageEvent
    )
    {
        if (event.key === AuthService.MFA_VERIFIED_STATE_KEY)
        {
            this.syncVerifiedFlagFromStorage();
        }
    }

    /**
     * @desc   - clear local and session storage values
     * @access - private
     * @param  - string preserveMfaUId : keep MFA state for this user
     * @return -
     */
    private clearAllStorageData
    (
        preserveMfaUId?: string
    )
    {
        let preservedMfaState: string | null;

        preservedMfaState = null;

        if (preserveMfaUId)
        {
            preservedMfaState =
                this.getPreservedMfaStateForUser(preserveMfaUId);
        }

        try
        {
            sessionStorage.clear();
            localStorage.clear();

            if (preservedMfaState)
            {
                localStorage.setItem(
                    AuthService.MFA_VERIFIED_STATE_KEY,
                    preservedMfaState
                );
            }
        }
        catch (e)
        {
            /* ignore quota / access errors */
        }
    }

    /**
     * @desc   - validate post-logout return URL safety
     * @access - private
     * @param  - string returnUrl : redirect URL candidate
     * @return - boolean
     */
    private isSafeReturnUrl
    (
        returnUrl: string
    ): boolean
    {
        let isSafe: boolean;

        isSafe = false;

        if (returnUrl &&
            returnUrl.startsWith('/') &&
            !returnUrl.startsWith('//'))
        {
            isSafe = true;
        }

        return isSafe;
    }

    /**
     * @desc   - store user/session data after successful login
     * @access - private
     * @param  - object firstResponse : auth response payload
     *         - Observable request2 : settings request
     *         - Subject subject : completion notifier
     * @return -
     */
    private hydrateUserSession
    (
        firstResponse: any,
        request2: Observable<any>,
        subject: Subject<any>
    )
    {
        let user;
        let settings;
        let taskNumberSetting;
        let permissions;
        let preferences;

        this.allowBrowserSessionPersistence = true;
        this.userSessionHydrationInProgress = true;

        user = firstResponse.cyData;
        this.cypherSessionIdValue = user?.sessionId || '';
        this.clearAllStorageData(user?.uId);
        this.settingService.setSettings([]);
        permissions = user?.permissions || [];
        preferences = user?.preferences || [];
        this.permissionsService.setPermissions(permissions);
        this.preferenceService.setPreferences(preferences);
        localStorage.setItem('user', JSON.stringify(user));
        this.user$.next(user);
        this.syncVerifiedFlagFromStorage();

        this.userSessionHydrationInProgress = false;

        delete user.permissions;
        delete user.preferences;

        request2.subscribe(
        {
            next: (secondResponse: any) =>
            {
                settings = secondResponse?.cyData?.settings ||
                    secondResponse?.settings;

                if (settings)
                {
                    this.settingService.setSettings(settings);
                    taskNumberSetting = Setting.getTaskNumberCode(settings);

                    if (taskNumberSetting)
                    {
                        this.settingService.setTaskNumberGen(
                            taskNumberSetting.value
                        );
                    }
                }

                subject.next(firstResponse);
            },
            error: () =>
            {
                subject.next(firstResponse);
            }
        });
    }

    //___________________ P U B L I C  M E T H O D S ___________________________

    /**
     * @desc   - save return URL used after authentication
     * @access - public
     * @param  - string returnUrlValue : return URL
     * @return -
     */
    public set returnUrl
    (
        returnUrlValue
    )
    {
        this.returnUrlValue = returnUrlValue;
    }

    /**
     * @desc   - get current return URL value
     * @access - public
     * @param  -
     * @return - string returnUrlValue : return URL
     */
    public get returnUrl()
    {
        return (this.returnUrlValue);
    }

    /**
     * @desc   - retrieve current user from auth state
     * @access - public
     * @param  -
     * @return - User : current user object
     */
    public get userValue(): User
    {
        return this.user$.getValue();
    }

    /**
     * @desc   - cookie fallback session id for cross-origin API calls
     * @access - public
     * @param  -
     * @return - string session id or empty
     */
    public get cypherSessionId(): string
    {
        return this.cypherSessionIdValue || '';
    }

    /**
     * @desc   - false after logout until login hydrate runs; blocks
     *           preference/settings writes that race after clear
     * @access - public
     * @param  -
     * @return - boolean
     */
    public mayPersistSessionToBrowser
    (
    ): boolean
    {
        let result: boolean;

        result = this.allowBrowserSessionPersistence;

        return result;
    }

    /**
     * @desc   - true while login hydrate runs before user$.next completes
     *           all dependent writes
     * @access - public
     * @param  -
     * @return - boolean
     */
    public isUserSessionHydrationInProgress
    (
    ): boolean
    {
        let result: boolean;

        result = this.userSessionHydrationInProgress;

        return result;
    }

    /**
     * @desc   - get access token from current user state
     * @access - public
     * @param  -
     * @return - string tokenValue : access token value
     */
    public getToken() : String
    {
        let tokenValue;

        tokenValue = '';

        return tokenValue;
    }

    /**
     * @desc   - set two-factor verification completion flag
     * @access - public
     * @param  - boolean isVerified : verification status
     * @return -
     */
    public setVerifiedStatus
    (
        isVerified: boolean
    ): void
    {
        let state: any;
        let uId: string;

        uId = this.userValue?.uId || '';
        this.verifiedFlag = isVerified;

        if (isVerified && uId)
        {
            state =
            {
                uId: uId,
                verified: true,
                verifiedAt: Date.now()
            };
            localStorage.setItem(
                AuthService.MFA_VERIFIED_STATE_KEY,
                JSON.stringify(state)
            );
        }
        else
        {
            localStorage.removeItem(
                AuthService.MFA_VERIFIED_STATE_KEY
            );
        }

        sessionStorage.removeItem(
            AuthService.MFA_VERIFIED_STORAGE_KEY
        );
    }

    /**
     * @desc   - get current two-factor verification flag
     * @access - public
     * @param  -
     * @return - boolean : true when verification is complete
     */
    public isVerified(): boolean
    {
        return this.verifiedFlag;
    }

    /**
     * @desc   - reset two-factor verification state
     * @access - public
     * @param  -
     * @return -
     */
    public clearVerificationState(): void
    {
        this.verifiedFlag = false;
        localStorage.removeItem(
            AuthService.MFA_VERIFIED_STATE_KEY
        );
        sessionStorage.removeItem(
            AuthService.MFA_VERIFIED_STORAGE_KEY
        );
    }

    /**
     * @desc   - true when user must pass MFA verification screen
     * @access - public
     * @param  -
     * @return - boolean : MFA verification required
     */
    public requiresVerification
    (
    ): boolean
    {
        let isRequired: boolean;
        let hasUser: boolean;
        let isMultiFactorEnabled: boolean;
        let isTwoStepEnabled: boolean;
        let isVerified: boolean;

        isRequired = false;
        hasUser = this.userValue ? true : false;
        isMultiFactorEnabled = this.settingService.isMultiFactorEnabled();
        isTwoStepEnabled = this.preferenceService.isTwoStepAuthEnabled();
        isVerified = this.isVerified();

        if (hasUser &&
            isMultiFactorEnabled &&
            isTwoStepEnabled &&
            !isVerified)
        {
            isRequired = true;
        }

        return isRequired;
    }

    /**
     * @desc   - true while logout is in progress; guards should skip MFA route
     * @access - public
     * @param  -
     * @return - boolean
     */
    public isLogoutInProgress
    (
    ): boolean
    {
        let result: boolean;

        result = this.logoutInProgressFlag === true;

        return result;
    }

    /**
     * @desc   - clear the mustChangePassword flag
     *           after a successful password update
     * @access - public
     * @param  -
     * @return -
     */
    public clearMustChangePassword(): void
    {
        let userData;

        if (this.userValue)
        {
            this.userValue.mustChangePassword =
                false;
        }

        userData = JSON.parse(
            localStorage.getItem('user')
        );

        if (userData)
        {
            userData.mustChangePassword = false;
            localStorage.setItem(
                'user',
                JSON.stringify(userData)
            );
        }
    }

    /**
     * @desc   - get authorization header
     * @access - public
     * @param  -
     * @return - object header : token header object for the
     *                           web services
     */
    public getAuthorizationHeader
    (
        isFormData
    ): Object
    {
        let header;

        if (isFormData)
        {
            header = ({Accept: 'application/json'});
        }
        else
        {
            header = ({Accept: 'application/json',
                               'Content-Type': 'application/json'});
        }

        return header
    }

    /**
     * @desc   - POST token/refresh with session cookie; new access token is
     *           stored server-side on cySession (no tokens in response body).
     * @access - public
     * @param  -
     * @return - Observable : cyData from successful refresh
     */
    public refreshToken
    (
    ): Observable<any>
    {
        let result;

        if (!this.tokenRefreshShared$)
        {
            this.tokenRefreshShared$ =
                this.apiService.post<any>(
                    USER_API.REFRESH_TOKEN,
                    {}
                ).pipe(
                    finalize(() =>
                    {
                        this.tokenRefreshShared$ = null;
                    }),
                    shareReplay(
                        {
                            bufferSize: 1,
                            refCount: true
                        }
                    )
                );
        }

        result = this.tokenRefreshShared$;

        return result;
    }

    /**
     * @desc   - verify access when a URL with query parameters are passed
     * @access - public
     * @param  - string accessKey : access key for the URL, the accessKey
     *                              is set up from a trusted domain
     * @param  - string username : username
     * @return - Observable httpRequest : observable object to process login
     */
    verifyAccess
    (
        accessKey: string,
        username: string
    ) : Observable<{}>
    {
        let data;
        let user;
        let settings;
        let taskNumberSetting;
        let permissions;
        let preferences;
        let request1;
        let request2;
        let subject;

        subject = new Subject();

        request2 = this.apiService.post<any>(SETTING_API.GET_SETTINGS, {});

        data =
        {
            accessKey: accessKey,
            username: username
        };

        request1 = this.apiService.post<any>(USER_API.USER_LOGIN, data);

        request1.subscribe((firstResponse: any) =>
        {
            let nextUId: string;

            this.logoutInProgressFlag = false;
            nextUId = firstResponse?.cyData?.uId || '';

            if (this.userValue?.uId &&
                this.userValue.uId !== nextUId)
            {
                this.clearVerificationState();
            }

            this.hydrateUserSession(firstResponse, request2, subject);
            return firstResponse;

        },
        error =>
        {
            subject.error(error);
        });

/*
        .pipe(map((response: any) =>
        {
            user = response.cyData;
            permissions = user.permissions;
            preferences = user.preferences;
            this.permissionsService.setPermissions(permissions);
            this.preferenceService.setPreferences(preferences);

            delete user.permissions;

            // store user details and basic auth credentials
            // in local storage to keep user logged
            // in between page refreshes
            localStorage.setItem('user', JSON.stringify(user));
            this.user$.next(user);

            return user;
        }));
*/
        return subject;
    }

    /**
     * @desc   - process user login from the login screen
     * @access - public
     * @param  - string username : username
     * @param  - string password : password
     * @return - Observable httpRequest : observable object to process login
     */
    login
    (
        username: string,
        password: string
    ) : Observable<any[]>
    {
        let user;
        let settings;
        let taskNumberSetting;
        let permissions;
        let preferences;
        let request1;
        let request2;
        let subject;

        subject = new Subject();

        // request1 = this.apiService
        //            .post<any>(USER_API.USER_LOGIN, {username, password});

        request1 = this.apiService.postRaw<any>(
            USER_API.USER_LOGIN,
            {
                username,
                password
            }
        );

        request2 = this.apiService.post<any>(SETTING_API.GET_SETTINGS, {});

        request1.subscribe((firstResponse: any) =>
        {
            this.logoutInProgressFlag = false;
            this.clearVerificationState();
            this.hydrateUserSession(firstResponse, request2, subject);
            return firstResponse;

        },
        error =>
        {
            subject.error(error);
        });

/*
 *  =====================================================
 *  Keep this portion of code as an example for forkJoin
 *  =====================================================
        allRequests = forkJoin([request1, request2]);

        allRequests.subscribe(response =>
        {
            console.log(response[1]);
            user = response[0].cyData;
            permissions = user.permissions;
            this.permissionsService.setPermissions(permissions);
            this.preferenceService.setPreferences(user.preferences);

            delete user.permissions;
            delete user.preferences;

            settings = response[1].cyData;

            if (settings)
            {
                this.settingService.setSettings(settings);
                taskNumberSetting = Setting.getTaskNumberCode(settings);

                if (taskNumberSetting)
                {
                    this.settingService.setTaskNumberGen(
                            taskNumberSetting.value);
                }
            }


            // store user details and basic auth credentials
            // in local storage to keep user logged
            // in between page refreshes
            // user.authdata = window.btoa(username + ':' + password);
            localStorage.setItem('user', JSON.stringify(user));
            this.user$.next(user);

            subject.next();

            return user;
        });
 */
        return subject;
    }

    /**
     * @desc   - build server SSO start URL for browser redirect
     * @access - public
     * @param  - string prompt : Azure prompt value
     *         - string returnUrl : post-login route
     * @return - string startUrl : server endpoint URL
     */
    public getSsoStartUrl
    (
        prompt: string,
        returnUrl: string
    ): string
    {
        let endpointUrl;
        let query;
        let startUrl;

        endpointUrl = `${environment.apiUrl}/${USER_API.SSO_START_WEB}`;
        query = new URLSearchParams();
        query.set('prompt', prompt || '');
        query.set('returnUrl', returnUrl || '');
        startUrl = `${endpointUrl}?${query.toString()}`;

        return startUrl;
    }

    /**
     * @desc   - complete session-based SSO login after redirect
     * @access - public
     * @param  -
     * @return - Observable subject : hydrated login response
     */
    public completeSsoSessionLogin
    (
    ): Observable<any>
    {
        let request1;
        let request2;
        let subject;

        subject = new Subject();
        request1 = this.apiService.postRaw<any>(
            USER_API.SSO_SESSION_LOGIN,
            {}
        );
        request2 = this.apiService.post<any>(SETTING_API.GET_SETTINGS, {});

        request1.subscribe(
        {
            next: (firstResponse: any) =>
            {
                if (firstResponse?.error)
                {
                    subject.error(firstResponse);
                    return;
                }

                this.clearVerificationState();
                this.logoutInProgressFlag = false;
                this.hydrateUserSession(firstResponse, request2, subject);
            },
            error: (error) =>
            {
                subject.error(error);
            }
        });

        return subject;
    }

    /**
     * @desc   - routes back to Cypher server
     * @access - public
     * @param  - pageInfo: information attached to the route
     * @return -
     */
    routeToCourt
    (
        pageInfo
    )
    {
        window.location.href = `${environment.courtUrl}/${pageInfo}`;
    }

    /**
     * @desc   - log out user
     * @access - public
     * @param  - LogoutReason reason : why logout was triggered
     * @return -
     */
    logout
    (
        reason?: LogoutReason
    )
    {
        let user: any;
        let uId: string;
        let data: Object;
        let subscription;

        this.idle.stop();
        this.logoutInProgressFlag = true;
        this.logoutReasonValue =
            reason || LOGOUT_REASON.MANUAL;

        user = this.userValue;
        uId = user?.uId;
        data =
        {
            uId: uId
        };

        // Clear client state first so route guards never bounce through MFA
        // during logout transitions. Server logout runs in background.
        this.clearSessionAndRedirect();

        subscription = this.apiService
            .post<any>(
                USER_API.USER_LOGOUT,
                data
            )
        .subscribe(
        {
            next: () =>
            {},
            error: () =>
            {}
        });
    }

    /**
     * @desc   - clears local storage and redirects
     *           to login or returnUrl
     * @access - private
     * @param  -
     * @return -
     */
    private clearSessionAndRedirect()
    {
        let returnUrl: string;
        let savedReturnUrl: string;
        let isSafeReturnUrl: boolean;
        let logoutReason: LogoutReason;
        let loginQueryParams: Record<string, string>;

        savedReturnUrl = this.returnUrl;
        isSafeReturnUrl = false;
        logoutReason = this.logoutReasonValue;
        loginQueryParams = {};

        if (!savedReturnUrl)
        {
            savedReturnUrl =
                localStorage.getItem('returnUrl');
        }

        this.allowBrowserSessionPersistence = false;
        this.userSessionHydrationInProgress = false;
        this.cypherSessionIdValue = '';
        this.clearVerificationState();

        this.user$.next(null);

        this.clearAllStorageData();
        this.preferenceService.clearPersistedStateOnLogout();
        this.permissionsService.clearPersistedStateOnLogout();
        this.settingService.clearPersistedStateOnLogout();

        isSafeReturnUrl =
            this.isSafeReturnUrl(savedReturnUrl);
        if (!isSafeReturnUrl)
        {
            savedReturnUrl = '';
        }

        returnUrl = savedReturnUrl;
        this.returnUrl =
            returnUrl ? returnUrl : '';

        if (this.returnUrl)
        {
            window.location.href = this.returnUrl;
        }
        else
        {
            if (logoutReason !== LOGOUT_REASON.MANUAL)
            {
                loginQueryParams[LOGOUT_QUERY_PARAM] =
                    logoutReason;
            }

            this.router.navigate(
                ['/login'],
                {
                    queryParams: loginQueryParams
                }
            ).then(() =>
            {
                this.logoutInProgressFlag = false;
            });
        }
    }

    /**
     * @desc   - Generate verification code for MFA
     * @access - public
     * @param  -
     * @return - Observable serviceObj
     */
    generateCode(): Observable<any>
    {
        let serviceObj: Observable<any>;

        serviceObj = this.apiService.post<any>(USER_API.GENERATE_CODE, {});

        return serviceObj;
    }

    /**
     * @desc   - Verify the MFA code
     * @access - public
     * @param  - Integer code : the code to verify
     * @return - Observable serviceObj
     */
    verifyCode
    (
        code: string
    ): Observable<any>
    {
        let serviceObj: Observable<any>;
        let postData: Object;

        postData =
        {
            code: code
        }

        serviceObj = this.apiService.post<any>(USER_API.VERIFY_CODE, postData);

        return serviceObj;
    }

    /**
     * @desc   - Check if current user is a super user
     * @access - public
     * @param  -
     * @return - boolean : true if user is admin, false otherwise
     */
    public isSuper(): boolean
    {
        let user : any;
        let isSuper: boolean;

        user = this.userValue;
        isSuper = user && (user.typeREF === USER_TYPE.SUPER);

        return isSuper;
    }

    /**
     * @desc   - Check if current user is an admin
     * @access - public
     * @param  -
     * @return - boolean : true if user is admin, false otherwise
     */
    public isAdmin(): boolean
    {
        let user : any;
        let isAdmin: boolean;

        user = this.userValue;
        isAdmin = user && (user.typeREF === USER_TYPE.ADMIN);

        return isAdmin;
    }

     /**
     * @desc   - Check if current user is regular user
     * @access - public
     * @param  -
     * @return - boolean : true if user is a regular user, false otherwise
     */
    public isRegular(): boolean
    {
        let user : any;
        let isRegular: boolean;

        user = this.userValue;
        isRegular = user && (user.typeREF === USER_TYPE.REGULAR);

        return isRegular;
    }

    /**
     * @desc   - Check if current user has a court role
     * @access - public
     * @param  -
     * @return - boolean : true if user is court role, false otherwise
     */
    public isCourtRole(): boolean
    {
        let user;
        let isCourtRole: boolean;

        user = this.userValue;

        isCourtRole =
            user && (user.role === USER_ROLE.COURT_STAFF ||
                     user.role === USER_ROLE.COURT_ADMIN);

        return isCourtRole;
    }
    /**
     * @desc   - Check if current user has a support role
     * @access - public
     * @param  -
     * @return - boolean : true if user is court role, false otherwise
     */
    public isSupportRole(): boolean
    {
        let user;
        let isSupportRole: boolean;

        user = this.userValue;

        isSupportRole =
            user && (user.role === USER_ROLE.SUPPORT);

        return isSupportRole;
    }

    /**
     * @desc   - Check if current user has a prosecutor role
     * @access - public
     * @param  -
     * @return - boolean : true if user is prosecutor, false otherwise
     */
    public isProsecutor(): boolean
    {
        let user;
        let isProsecutor: boolean;

        user = this.userValue;

        isProsecutor =
            user && (user.role === USER_ROLE.PROSECUTOR);

        return isProsecutor;
    }
}

export { AuthService }